import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import { OptionIcon } from '@app/src/components/Form/Select'
import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import { FinalRiskRatingIcon } from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import {
  responseItemOrganizationFacetSettings,
  responseItemPeriodFacetSettings,
  responseItemProviderIdFacetSettings,
  responseItemQuestionFacetSettings,
  responseItemQuestionnaireFacetSettings,
} from '@app/src/pages/ResourceCollection/Filters/Common/ResponseItemFacetSettings'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterFacetSelect from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import type { FiltersContextProps } from '@app/src/pages/ResourceCollection/Filters/Filters'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import FilterSelect from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import { PeriodFilterOptions } from '@app/src/pages/ResourceCollection/Filters/PeriodFilterDisplay'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { QuestionType } from '@app/src/types/reporting'
import { RequestTargetType } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { AmplitudeTrackingEvents, Solutions } from '@app/src/wf-constants'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'
import { Box, Button, Chip, Grid, Tooltip } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import { LatestAnswersOnly } from '../Collections/LatestAnswersOnly'
import AllFiltersButton from './AllFiltersButton'
import AssessmentTemplateSelectorWithLevelFilter from './AssessmentMiniFilter/AssessmentTemplateSelectorWithLevelFilter'
import useLocalisedCountryFilter from './Common/useLocalisedCountryFilter'
import CustomCategoriesFilters from './CustomCategoriesFilters'
import OptionWithLabel from './OptionWithLabel'
import RangeFilter from './RangeFilter'
import SpendFilter from './SpendFilter'
import StatusOption from './StatusOption'
import TextFieldFilter from './TextFieldFilter'

export interface FiltersComponentsProps {
  allowedFilters: FiltersContextProps['allowedFilters']
  implicitFilters?: FilterGroup[]
  onlyLatest: boolean
  setOnlyLatest: React.Dispatch<React.SetStateAction<boolean>>
}

const DataHubFiltersAllAnswers: React.FC<FiltersComponentsProps> = ({
  allowedFilters,
  implicitFilters: baseImplicitFilters = [],
  onlyLatest,
  setOnlyLatest,
}) => {
  const { renderWithPermission } = usePermissions()
  const autocompleteClasses = useAutocompleteStyles()
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { hasPermission } = usePermissions()
  const verificationEnabled = hasPermission(Permissions.WRITE_VERIFICATION)
  const { solution } = useAuthentication().scope
  const { trackEvent } = useAmplitude()
  const { productMapping } = useFlags()
  const isAiScreeningEnabled = useAiScreeningEnabled()
  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const implicitFilters = [...insertIf(onlyLatest, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER), ...baseImplicitFilters]
  const { renderOption, renderTags, customFilterOption, customSort } = useLocalisedCountryFilter()

  const isProviderSpecific = implicitFilters.some(
    filter => filter.name === 'response.request.subscriptions.targetProviderId',
  )
  const lg = isProviderSpecific ? 3.5 : 2.5

  return (
    <Grid container spacing={1}>
      <Filters allowedFilters={allowedFilters}>
        {({ clearQueryFilter, inDrawerFiltersCount, isFiltered }) => {
          return (
            <>
              <Grid item lg={lg} md={4} xs={12}>
                <Filter name="response.request.template.id" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      multiple
                      facetsParam={responseItemQuestionnaireFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                      size="small"
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'resourceTypes.questionnaires' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item lg={lg} md={4} xs={12}>
                <Filter name="requestItem.template.id" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={responseItemQuestionFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'schemas.filter.question' })}
                      value={[value].flat()}
                      {...PeriodFilterOptions}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item lg={2} md={2} sm={4} xs={6}>
                <Filter name="response.request.periodName" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={responseItemPeriodFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                      filterName={filterName}
                      onChange={(value: string[]) => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                      value={[value].flat()}
                      {...PeriodFilterOptions}
                    />
                  )}
                </Filter>
              </Grid>
              {!isProviderSpecific && (
                <Grid item lg={2} md={2} sm={4} xs={6}>
                  <Filter name="response.request.subscriptions.targetProviderId" operator={Operators.In}>
                    {({ value, onChange, filterName }) => (
                      <FilterFacetSelect
                        implicitFilters={implicitFilters}
                        multiple
                        facetsParam={responseItemOrganizationFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                        size="small"
                        filterName={filterName}
                        onChange={onChange}
                        fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                        value={[value].flat()}
                      />
                    )}
                  </Filter>
                </Grid>
              )}
              <Grid item lg={1} md={2} sm={2} xs={6}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2} mt={2}>
                            <Filter name="response.request.periodName" operator={Operators.In}>
                              {({ value, onChange, filterName }) => (
                                <FilterFacetSelect
                                  implicitFilters={implicitFilters}
                                  size="small"
                                  multiple
                                  facetsParam={responseItemPeriodFacetSettings([
                                    FetchKey.ResponseItemsFacets,
                                    filterName,
                                  ])}
                                  filterName={filterName}
                                  onChange={(value: string[]) => onChange(value)}
                                  fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                                  value={[value].flat()}
                                  {...PeriodFilterOptions}
                                />
                              )}
                            </Filter>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.allAnswers' })}>
                              <Filter name="requestItem.template.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={responseItemQuestionFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.question' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            {!isProviderSpecific && (
                              <FilterSection label={formatMessage({ id: 'schemas.filter.companyDetails' })}>
                                <Filter name="response.request.subscriptions.targetProviderId" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      multiple
                                      facetsParam={responseItemProviderIdFacetSettings([
                                        FetchKey.ResponseItemsFacets,
                                        filterName,
                                      ])}
                                      size="small"
                                      filterName={filterName}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyId' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter name="response.request.subscriptions.targetProviderId" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      multiple
                                      facetsParam={responseItemOrganizationFacetSettings([
                                        FetchKey.ResponseItemsFacets,
                                        filterName,
                                      ])}
                                      size="small"
                                      filterName={filterName}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter name="response.request.subscriptions.target.country.id" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseItemsFacets, filterName],
                                        endpoint: endpoints.responseItemsWithFacets,
                                        facetsParam: [
                                          { name: 'response.request.subscriptions.target.country.iso3166Alpha2' },
                                        ],
                                        filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                      }}
                                      renderOption={renderOption}
                                      renderTags={renderTags}
                                      size="small"
                                      filterName={filterName}
                                      filterOptions={customFilterOption}
                                      customSort={customSort}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.country.countryCode' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                            <FilterSection label={formatMessage({ id: 'schemas.filter.requestDetails' })}>
                              <Filter name="response.request.template.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={responseItemQuestionnaireFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.request.name' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              {productMapping && (
                                <>
                                  <Filter name="response.request.subscriptions.targetType" operator={Operators.EqualTo}>
                                    {({ value, onChange }) => (
                                      <FilterSelect
                                        size="small"
                                        multiple={false}
                                        options={[
                                          {
                                            value: RequestTargetType.Product,
                                            label: formatMessage({ id: 'schemas.mappingNode.product' }),
                                          },
                                          {
                                            value: RequestTargetType.Provider,
                                            label: formatMessage({ id: 'resourceCollections.general.company' }),
                                          },
                                        ]}
                                        onChange={value => onChange(value)}
                                        fieldLabel={formatMessage({ id: 'reporting.requestedFor' })}
                                        value={value as string}
                                      />
                                    )}
                                  </Filter>
                                  <Filter name="response.request.subscriptions.targetProductId" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: 'response.request.subscriptions.targetProduct.name' }],
                                        }}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.mappingNode.product' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              <Filter name="requestItem.section.title" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'requestItem.section.title', isEnum: true }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.responseItem.section' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.answersDetails' })}>
                              {verificationEnabled && (
                                <>
                                  <Filter name="verifications.externalVerificationStatus" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: filterName }],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        size="small"
                                        renderOption={(props, option, { selected }) => (
                                          <StatusOption
                                            key={option.label}
                                            props={props}
                                            option={option}
                                            selected={selected}
                                            verification
                                          />
                                        )}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({
                                          id: 'filters.responseItem.externalVerificationStatus',
                                        })}
                                        value={[value].flat()}
                                        skipNotSet={false}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Tooltip
                                                  key={option?.value?.toString() || index}
                                                  title={formatMessage({
                                                    id: `reporting.reviewStatuses.${option.label}`,
                                                  })}
                                                  classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                                  arrow
                                                >
                                                  <Chip
                                                    label={formatMessage({
                                                      id: `reporting.reviewStatuses.${option.label}`,
                                                    })}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                  />
                                                </Tooltip>
                                              ),
                                          )
                                        }
                                      />
                                    )}
                                  </Filter>
                                  <Filter name="verifications.internalVerificationStatus" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: filterName }],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        size="small"
                                        renderOption={(props, option, { selected }) => (
                                          <StatusOption
                                            key={option.label}
                                            props={props}
                                            option={option}
                                            selected={selected}
                                            verification
                                          />
                                        )}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({
                                          id: 'filters.responseItem.internalVerificationStatus',
                                        })}
                                        value={[value].flat()}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Tooltip
                                                  key={option?.value?.toString() || index}
                                                  title={formatMessage({
                                                    id: `reporting.internalVerificationStatuses.${option.label}`,
                                                  })}
                                                  classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                                  arrow
                                                >
                                                  <Chip
                                                    label={formatMessage({
                                                      id: `reporting.internalVerificationStatuses.${option.label}`,
                                                    })}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                  />
                                                </Tooltip>
                                              ),
                                          )
                                        }
                                        skipNotSet={false}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              <RangeFilter
                                uniqueIdFrom="responseDateFrom"
                                uniqueIdTo="responseDateTo"
                                name="response.submittedAt"
                                fieldLabelFrom={formatMessage({ id: 'schemas.response.responseDateFrom' })}
                                fieldLabelTo={formatMessage({ id: 'schemas.response.responseDateTo' })}
                              />
                              <Filter name="answer" operator={Operators.Contains}>
                                {({ filterName, operator, value }) => (
                                  <TextFieldFilter
                                    defaultValue={value}
                                    value={value}
                                    filterName={filterName}
                                    operator={operator}
                                    size="small"
                                    fullWidth
                                    label={formatMessage({ id: 'schemas.filter.responseItemValue' })}
                                  />
                                )}
                              </Filter>
                              <Filter name="answerStatus" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'answerStatus' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.label}
                                        props={props}
                                        label={formatMessage({ id: `reporting.answerStatusShort.${option.label}` })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({
                                                id: `reporting.answerStatusShort.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'reporting.skipQuestion.skippedQuestion' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="requestItem.questionTypeId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'requestItem.questionType.name' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.value?.toString()}
                                        props={props}
                                        label={formatMessage({ id: `reporting.questionType.${option.label}` })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({ id: `reporting.questionType.${option.label}` })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={selectedNames =>
                                      onChange(
                                        (selectedNames as string[]).map(name =>
                                          String(QuestionType[name as keyof typeof QuestionType]),
                                        ),
                                      )
                                    }
                                    fieldLabel={formatMessage({ id: 'schemas.filter.questionType' })}
                                    value={[value]
                                      .flat()
                                      .map(id => QuestionType[Number(id)])
                                      .filter(Boolean)}
                                  />
                                )}
                              </Filter>
                              <Filter name="response.hasExpiredFiles" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'response.hasExpiredFiles', isEnum: true }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.value?.toString()}
                                        props={props}
                                        label={
                                          option.value === true
                                            ? formatMessage({ id: 'filter.true' })
                                            : option.value === false
                                              ? formatMessage({ id: 'filter.false' })
                                              : String(option.label)
                                        }
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={
                                                option.value === true
                                                  ? formatMessage({ id: 'filter.true' })
                                                  : option.value === false
                                                    ? formatMessage({ id: 'filter.false' })
                                                    : String(option.label)
                                              }
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.expiredFiles' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            {isAiScreeningEnabled && (
                              <FilterSection label={formatMessage({ id: 'schemas.request.source' })}>
                                <Filter operator={Operators.EqualTo} name="isScraped">
                                  {({ value, onChange }) => (
                                    <FilterSelect
                                      size="small"
                                      multiple={false}
                                      options={[
                                        { value: true, label: formatMessage({ id: 'general.aiScreening' }) },
                                        { value: false, label: formatMessage({ id: 'general.selfReported' }) },
                                      ]}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.request.source' })}
                                      value={Array.isArray(value) ? value?.[0] : value}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                            <SpendFilter
                              spendFilterNamePrefix="response.request.subscriptions.target"
                              defaultCollapsed
                            />
                            {canAccessAssessmentTemplate && (
                              <AssessmentTemplateSelectorWithLevelFilter
                                fetchKey={FetchKey.ResponseItemsFacets}
                                facetEndpoint={endpoints.responseItemsWithFacets}
                                allowedFilters={allowedFilters}
                                assessmentFilterNamePrefix="response.request.subscriptions.target.assessments"
                              />
                            )}
                            <FilterSection
                              defaultCollapsed
                              label={formatMessage({ id: 'schemas.filter.additionalInformation' })}
                            >
                              <Filter
                                operator={Operators.In}
                                name="response.request.subscriptions.target.providerOwners.userId"
                              >
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [
                                        { name: 'response.request.subscriptions.target.providerOwners.user.name' },
                                      ],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.ownerUserIds' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter
                                operator={Operators.In}
                                name="response.request.subscriptions.target.finalRiskRating"
                              >
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'response.request.subscriptions.target.finalRiskRating' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <li {...props}>
                                        <OptionIcon selected={selected} multiple />
                                        <FinalRiskRatingIcon value={option?.label} />
                                        {formatMessage({
                                          id: option?.label
                                            ? `schemas.provider.finalRiskRatingValues.${option.label}`
                                            : 'general.notAvailable',
                                        })}
                                      </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              icon={<FinalRiskRatingIcon value={option.label} />}
                                              label={formatMessage({
                                                id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={value => {
                                      if (value.length) {
                                        trackEvent({
                                          name: AmplitudeTrackingEvents.Analyze.Filter.RiskAssessment,
                                          eventProps: {
                                            filter_value: value,
                                          },
                                        })
                                      }

                                      onChange(value)
                                    }}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter operator={Operators.In} name="response.request.subscriptions.target.priority">
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'response.request.subscriptions.target.priority' }],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.label}
                                        props={props}
                                        label={formatMessage({
                                          id: option?.label
                                            ? `schemas.provider.priorityValues.${option.label}`
                                            : 'general.notAvailable',
                                        })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({
                                                id: `schemas.provider.priorityValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.priority' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              {solution !== Solutions.Finance && (
                                <>
                                  <Filter
                                    operator={Operators.In}
                                    name="response.request.subscriptions.target.activityStatus"
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [
                                            { name: 'response.request.subscriptions.target.activityStatus' },
                                          ],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.activityStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.activityStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.activityStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                  <Filter
                                    operator={Operators.In}
                                    name="response.request.subscriptions.target.providerApprovalStatus"
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [
                                            { name: 'response.request.subscriptions.target.providerApprovalStatus' },
                                          ],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.providerApprovalStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.providerApprovalStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              {renderWithPermission({
                                [Permissions.SOURCING_USER]: (
                                  <>
                                    <Filter
                                      operator={Operators.In}
                                      name="response.request.subscriptions.target.supplierUsage"
                                    >
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseItemsFacets, filterName],
                                            endpoint: endpoints.responseItemsWithFacets,
                                            facetsParam: [
                                              { name: 'response.request.subscriptions.target.supplierUsage' },
                                            ],
                                            filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage({
                                                id: option?.label
                                                  ? `schemas.provider.supplierUsageValues.${option.label}`
                                                  : 'general.notAvailable',
                                              })}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage({
                                                      id: `schemas.provider.supplierUsageValues.${option.label}`,
                                                    })}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.supplierUsage' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                    <Filter operator={Operators.In} name="response.request.subscriptions.target.tier">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseItemsFacets, filterName],
                                            endpoint: endpoints.responseItemsWithFacets,
                                            facetsParam: [
                                              { name: 'response.request.subscriptions.target.tier', isEnum: true },
                                            ],
                                            filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage(
                                                {
                                                  id: `schemas.provider.tierValues`,
                                                },
                                                { tier: option?.label ?? '' },
                                              )}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage(
                                                      {
                                                        id: `schemas.provider.tierValues`,
                                                      },
                                                      { tier: option.label },
                                                    )}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.tier' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                  </>
                                ),
                              })}
                            </FilterSection>
                            <CustomCategoriesFilters baseFilterName="response.request.subscriptions.target" />

                            {productMapping && (
                              <FilterSection defaultCollapsed label={formatMessage({ id: 'navbar.productMapping' })}>
                                <Filter
                                  operator={Operators.In}
                                  name="response.request.subscriptions.target.mappingNodes.actorType.id"
                                >
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseItemsFacets, filterName],
                                        endpoint: endpoints.responseItemsWithFacets,
                                        facetsParam: [
                                          {
                                            name: 'response.request.subscriptions.target.mappingNodes.actorType.name',
                                          },
                                        ],
                                        filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                      }}
                                      renderOption={(props, option, { selected }) => (
                                        <OptionWithLabel
                                          key={option.label}
                                          props={props}
                                          label={formatMessage({
                                            id: option?.label
                                              ? `schemas.mappingNode.actorTypeValues.${option.label}`
                                              : 'general.notAvailable',
                                          })}
                                          multiple
                                          selected={selected}
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value?.map(
                                          (option, index) =>
                                            option && (
                                              <Chip
                                                {...getTagProps({ index })}
                                                key={option?.value?.toString() || index}
                                                label={formatMessage({
                                                  id: `schemas.mappingNode.actorTypeValues.${option.label}`,
                                                })}
                                                size="small"
                                              />
                                            ),
                                        )
                                      }
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.mappingNode.actorType' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter
                                  operator={Operators.In}
                                  name="response.request.subscriptions.target.mappingNodes.tier"
                                >
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseItemsFacets, filterName],
                                        endpoint: endpoints.responseItemsWithFacets,
                                        facetsParam: [
                                          {
                                            name: 'response.request.subscriptions.target.mappingNodes.tier',
                                            isEnum: true,
                                          },
                                        ],
                                        filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.mappingNode.tier' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                          </Box>
                        </DrawerViewFilters>,
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button variant="text" onClick={clearQueryFilter} sx={{ ml: 1 }}>
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
              <Grid item sm={2} xs={12}>
                <LatestAnswersOnly onlyLatestAnswers={onlyLatest} setOnlyLatestAnswers={setOnlyLatest} />
              </Grid>
              <Filter name="response.request.template.tags.tag" operator={Operators.In}>
                {({ value, onChange }) => (
                  <>
                    {value && (
                      <Grid item xs={12}>
                        <Chip label={`${value} requests`} onDelete={() => onChange(null)} />
                      </Grid>
                    )}
                  </>
                )}
              </Filter>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default DataHubFiltersAllAnswers
